/**
 * Pay Day rules and read-only summary text for a pay schedule (EPN-216, brief "Admin pay schedule / payday-dependent UI rules").
 *
 * Pure functions only. The numeric frequencies and the day / kind encoding mirror EPN.PayrollConfig.Api (PayFrequency, PayWeekday,
 * PayDayKind). "Annually" has NO backend frequency and no month storage today: it is modelled here so the selector set and the
 * summary are ready, but nothing in this module persists it. Rules the brief leaves undefined are NOT invented: each affected
 * frequency carries a `confirmationNote` that the UI shows as "Needs payroll confirmation".
 */

export type PayFrequencyKey = 'monthly' | 'quarterly' | 'weekly' | 'biweekly' | 'semimonthly' | 'annually';

/** Backend numeric PayFrequency values. Annually has none (BACKEND GAP). */
const FREQUENCY_BY_NUMBER: Record<number, PayFrequencyKey> = { 1: 'weekly', 2: 'biweekly', 3: 'semimonthly', 4: 'monthly', 5: 'quarterly' };

export const PAY_FREQUENCY_DISPLAY: Record<PayFrequencyKey, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  semimonthly: 'Twice a Month',
  annually: 'Annually',
};

export const END_OF_MONTH = 'eom';

export type PayDayField = 'first' | 'second' | 'month';

export interface PayDayOption { value: string; label: string }

export interface PayDayControl {
  field: PayDayField;
  label: string;
  options: PayDayOption[];
}

export interface PayDayRule {
  controls: PayDayControl[];
  /** Shown as "Needs payroll confirmation: ..." where the brief leaves a rule undefined. */
  confirmationNote: string | null;
  /** Plain explanation of an approved rule (not an open question). */
  hint?: string | null;
}

/** What the selectors hold. Values are option values: '1'..'28', 'eom', weekday '1'..'5', month '1'..'12'. '' = not chosen. */
export interface PayDaySelection { first: string; second: string; month: string }

export const EMPTY_PAY_DAY_SELECTION: PayDaySelection = { first: '', second: '', month: '' };

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function frequencyKey(frequency: number | PayFrequencyKey | null | undefined): PayFrequencyKey | null {
  if (frequency == null) return null;
  if (typeof frequency === 'number') return FREQUENCY_BY_NUMBER[frequency] ?? null;
  return frequency in PAY_FREQUENCY_DISPLAY ? frequency : null;
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const last = n % 10;
  return `${n}${last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'}`;
}

function dayRange(from: number, to: number, withEom: boolean): PayDayOption[] {
  const out: PayDayOption[] = [];
  for (let d = from; d <= to; d++) out.push({ value: String(d), label: ordinal(d) });
  if (withEom) out.push({ value: END_OF_MONTH, label: 'End of Month' });
  return out;
}

const WEEKDAY_OPTIONS: PayDayOption[] = [1, 2, 3, 4, 5].map((d) => ({ value: String(d), label: WEEKDAY_NAMES[d]! }));
const MONTH_OPTIONS: PayDayOption[] = MONTH_NAMES.map((m, i) => ({ value: String(i + 1), label: m }));

const HINT_QUARTERLY = 'Pay is issued once every three months. The pay day falls in the month that is three months after the Pay Period Start Date, on the day chosen here (e.g. a period starting January 1 with pay day 15 pays on April 15).';
const NOTE_BIWEEKLY = 'a bi-weekly anchor (first pay date or pay-period schedule) is needed to know which weeks are pay weeks. The weekday alone is not enough.';
const NOTE_SEMIMONTHLY = 'how pay days that fall on a weekend or holiday are treated is not defined yet.';
const NOTE_ANNUALLY = 'the actual disbursement day or date is not defined yet. Only the month is chosen.';

/** The selector set the admin sees for a frequency. */
export function getPayDayRule(frequency: PayFrequencyKey): PayDayRule {
  switch (frequency) {
    case 'monthly':
      return { controls: [{ field: 'first', label: 'Pay Day', options: dayRange(1, 28, true) }], confirmationNote: null };
    case 'quarterly':
      return { controls: [{ field: 'first', label: 'Pay Day', options: dayRange(1, 28, true) }], confirmationNote: null, hint: HINT_QUARTERLY };
    case 'weekly':
      return { controls: [{ field: 'first', label: 'Pay Day', options: WEEKDAY_OPTIONS }], confirmationNote: null };
    case 'biweekly':
      return { controls: [{ field: 'first', label: 'Pay Day', options: WEEKDAY_OPTIONS }], confirmationNote: NOTE_BIWEEKLY };
    case 'semimonthly':
      return {
        controls: [
          { field: 'first', label: 'First Pay Day', options: dayRange(1, 15, false) },
          { field: 'second', label: 'Second Pay Day', options: dayRange(16, 28, true) },
        ],
        confirmationNote: NOTE_SEMIMONTHLY,
      };
    case 'annually':
      return { controls: [{ field: 'month', label: 'Pay Month', options: MONTH_OPTIONS }], confirmationNote: NOTE_ANNUALLY };
  }
}

/** The fields of a PayrollConfig schedule that carry its pay days (server shape: kind 2 = last day of the month, day null). */
export interface PayDayFields {
  firstPayDay?: number | null | undefined;
  firstPayDayKind?: number | null | undefined;
  secondPayDay?: number | null | undefined;
  secondPayDayKind?: number | null | undefined;
}

function toValue(day: number | null | undefined, kind: number | null | undefined): string {
  if (kind === 2) return END_OF_MONTH;
  return day != null ? String(day) : '';
}

/** Selector values for a stored schedule. Annual month is never stored, so it is always empty here. */
export function selectionFromSchedule(s: PayDayFields): PayDaySelection {
  return { first: toValue(s.firstPayDay, s.firstPayDayKind), second: toValue(s.secondPayDay, s.secondPayDayKind), month: '' };
}

export interface PayDayPayload {
  firstPayDay: number | null;
  firstPayDayKind: number;
  secondPayDay: number | null;
  secondPayDayKind: number | null;
}

const KIND_DAY_OF_MONTH = 1;
const KIND_LAST_DAY = 2;

/** Request fields for the existing PayrollConfig contract (day + PayDayKind). Call only after validation passes. Not defined for annually. */
export function selectionToPayload(frequency: Exclude<PayFrequencyKey, 'annually'>, sel: PayDaySelection): PayDayPayload {
  const first = sel.first === END_OF_MONTH
    ? { day: null, kind: KIND_LAST_DAY }
    : { day: Number(sel.first), kind: KIND_DAY_OF_MONTH };
  if (frequency !== 'semimonthly') {
    return { firstPayDay: first.day, firstPayDayKind: first.kind, secondPayDay: null, secondPayDayKind: null };
  }
  const second = sel.second === END_OF_MONTH
    ? { day: null, kind: KIND_LAST_DAY }
    : { day: Number(sel.second), kind: KIND_DAY_OF_MONTH };
  return { firstPayDay: first.day, firstPayDayKind: first.kind, secondPayDay: second.day, secondPayDayKind: second.kind };
}

function optionValues(control: PayDayControl): string[] {
  return control.options.map((o) => o.value);
}

/** Field-level errors for a selection; empty object = valid. Mirrors the brief's ranges plus the server's existing 13-17 day semimonthly gap. */
export function validatePayDaySelection(frequency: PayFrequencyKey, sel: PayDaySelection): Partial<Record<PayDayField, string>> {
  const errors: Partial<Record<PayDayField, string>> = {};
  for (const control of getPayDayRule(frequency).controls) {
    const value = sel[control.field];
    if (value === '') errors[control.field] = `${control.label} is required.`;
    else if (!optionValues(control).includes(value)) errors[control.field] = `${control.label} is not one of the allowed choices.`;
  }
  if (frequency === 'semimonthly' && !errors.first && !errors.second && sel.second !== END_OF_MONTH) {
    const gap = Number(sel.second) - Number(sel.first);
    if (gap < 13 || gap > 17) errors.second = 'Second Pay Day should be approximately 13 to 17 days after the First Pay Day.';
  }
  return errors;
}

/** True when a stored value is not among the brief's options (e.g. an older schedule on day 30 or on a Saturday). */
export function isOutsideStandardOptions(frequency: PayFrequencyKey, sel: PayDaySelection): boolean {
  return getPayDayRule(frequency).controls.some((c) => sel[c.field] !== '' && !optionValues(c).includes(sel[c.field]));
}

function describeValue(frequency: PayFrequencyKey, field: PayDayField, value: string): string {
  if (value === END_OF_MONTH) return 'the last day of the month';
  const n = Number(value);
  if (field === 'month') return MONTH_NAMES[n - 1] ?? '';
  if (frequency === 'weekly' || frequency === 'biweekly') return WEEKDAY_NAMES[n] ?? '';
  return Number.isInteger(n) && n > 0 ? `the ${ordinal(n)}` : '';
}

export interface PayScheduleSummaryInput extends PayDayFields {
  frequency: number | PayFrequencyKey | null | undefined;
  /** 1-12. Annually only; no backend storage yet. */
  payMonth?: number | null | undefined;
}

/**
 * Read-only summary, e.g. "Monthly on the 15th", "Weekly on Fridays", "Twice a Month on the 1st and the last day of the month",
 * "Annually in March". Never states a date it does not know: quarterly shows only the day, bi-weekly only the weekday.
 * Returns '' when the frequency is unknown or the pay day is missing, so a caller can show its own placeholder.
 */
export function formatPaySchedule(input: PayScheduleSummaryInput): string {
  const key = frequencyKey(input.frequency);
  if (!key) return '';
  const label = PAY_FREQUENCY_DISPLAY[key];
  const sel = selectionFromSchedule(input);
  if (key === 'annually') {
    const month = input.payMonth != null ? MONTH_NAMES[input.payMonth - 1] : undefined;
    return month ? `${label} in ${month}` : '';
  }
  const first = describeValue(key, 'first', sel.first);
  if (!first) return '';
  if (key === 'weekly' || key === 'biweekly') return `${label} on ${first}s`;
  if (key === 'semimonthly') {
    const second = describeValue(key, 'second', sel.second);
    return second ? `${label} on ${first} and ${second}` : '';
  }
  return `${label} on ${first}`;
}
